import type { UserDeletionImpact, UserResponse } from '@shared/schemas';

import type { SaveStatus } from '../../_shared/domain/administrator-draft.model';

/**
 * Redux state for the Administrators admin index: the loaded rows, the load
 * lifecycle, and an independent archive lifecycle for the row action. Every field
 * is serialisable.
 *
 * The two failures are two fields, and that separation is a fix, not tidiness
 * (PI-50). They used to share one `error`: an archive that failed wrote the
 * message the LOAD banner read, and nothing cleared it, so a red "could not load"
 * banner sat on a perfectly loaded table; including after a later archive
 * succeeded. They also want opposite treatments: not knowing what the list holds
 * belongs in the table's own empty state, while an action that failed is transient
 * and belongs in a snackbar.
 */
export interface AdministratorsState {
  rows: UserResponse[];
  loading: boolean;
  /** The list could not be loaded. Shown by the table instead of "no rows". */
  loadError: string | null;
  archiveStatus: SaveStatus;
  /** The last archive attempt failed. Consumed by a snackbar, then cleared. */
  archiveError: string | null;
  /** Deletion's own lifecycle, for the same reason archive has one (PI-50). */
  deleteStatus: SaveStatus;
  /** The last delete attempt failed. Consumed by a snackbar, then cleared. */
  deleteError: string | null;
  /**
   * What the pending deletion takes (comments, API keys), for the dialog. Null
   * while unknown (loading, failed, or no dialog), and the dialog then words
   * it without figures rather than blocking on it.
   */
  deletionImpact: UserDeletionImpact | null;
}

export const initialState: AdministratorsState = {
  rows: [],
  loading: false,
  loadError: null,
  archiveStatus: 'idle',
  archiveError: null,
  deleteStatus: 'idle',
  deleteError: null,
  deletionImpact: null,
};
